import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Shield, Award, Clock, Users, CheckCircle, Star, PhoneCall, MapPin, Calendar, FileText, Trophy, Heart } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { MobileStickyFAB } from "@/components/MobileStickyFAB";
import { SEOHead } from "@/components/SEOHead";
import { CertificationsCredentials } from "@/components/CertificationsCredentials";
import { Testimonials } from "@/components/Testimonials";
import { TeamAbout } from "@/components/TeamAbout";

interface Project {
  slug: string;
  title: string;
  location: string;
  tags: string[];
  images: string[];
}

interface GalleryData {
  projects: Project[];
}

const WelcomeLanding = () => {
  const [galleryData, setGalleryData] = useState<GalleryData>({ projects: [] });
  const { toast } = useToast();

  useEffect(() => {
    const fetchGalleryData = async () => {
      try {
        const response = await fetch('/gallery.json');
        const data = await response.json();
        setGalleryData(data);
      } catch (error) {
        console.error('Error fetching gallery data:', error);
      }
    };

    fetchGalleryData();
  }, []);

  // Why choose us advantages over competitors
  const competitiveAdvantages = [
    {
      advantage: "50-Year Warranty",
      us: "Comprehensive 50-year manufacturer warranty",
      others: "Typical 20-25 year warranty",
      icon: <Shield className="w-6 h-6 text-green-600" />
    },
    {
      advantage: "24/7 Emergency Response", 
      us: "Available 24/7 for emergency repairs",
      others: "Business hours only",
      icon: <Clock className="w-6 h-6 text-green-600" />
    },
    {
      advantage: "Insurance Claims Expertise",
      us: "We handle all insurance paperwork",
      others: "You handle insurance yourself", 
      icon: <FileText className="w-6 h-6 text-green-600" />
    },
    {
      advantage: "GAF Master Elite Certification",
      us: "Top 2% of roofing contractors nationwide",
      others: "Basic contractor license only",
      icon: <Award className="w-6 h-6 text-green-600" />
    }
  ];

  // Success stories with local focus
  const successStories = [
    {
      customer: "The Johnson Family",
      location: "Bay Shore, NY",
      problem: "Storm damage left their roof leaking in multiple areas",
      solution: "Complete roof replacement with GAF UHDZ shingles",
      result: "No leaks for 3 years, increased home value by $25,000",
      image: "/images/projects/residential-roof-replacement.jpg",
      quote: "Safeway transformed our damaged roof into something beautiful. The process was smooth and the quality is outstanding."
    },
    {
      customer: "Thompson Commercial Plaza",
      location: "Hempstead, NY", 
      problem: "Aging commercial roof causing interior damage",
      solution: "TPO membrane system installation",
      result: "Zero maintenance issues, 30% energy savings",
      image: "/images/projects/commercial-roofing-project.jpg",
      quote: "Professional, on-time, and within budget. Couldn't ask for more from a roofing contractor."
    }
  ];

  return (
    <>
      <SEOHead
        title="Welcome - Why 5,000+ Homeowners Choose Safeway Roofing"
        description="See why Long Island families trust Safeway Roofing. GAF Master Elite certified, 50-year warranties, 24/7 emergency service. View credentials, gallery & testimonials."
        canonical="https://welcome.safewayroofingandchimney.com"
        structuredData={{
          "@type": "LocalBusiness",
          "name": "Safeway Roofing & Chimney",
          "telephone": "+1-800-645-6676"
        }}
      />
      
      <main className="min-h-screen bg-background">
        {/* Trust-Focused Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-blue-600 to-blue-800">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white space-y-8">
                <img 
                  src="/assets/logos/safeway-logo.png" 
                  alt="Safeway Roofing & Chimney Logo" 
                  className="h-20 w-auto"
                />
                
                <div className="space-y-6">
                  <h1 className="text-4xl lg:text-5xl font-bold leading-tight">
                    Welcome — Here's Why 5,000+ Long Island Families Choose{" "}
                    <span className="text-blue-200">Safeway Roofing</span>
                  </h1>
                  
                  <p className="text-xl text-blue-100 leading-relaxed">
                    Thank you for requesting an estimate. Now let us show you why we're the trusted choice 
                    for your roofing project — with proven credentials, quality work, and unmatched warranties.
                  </p>
                </div>

                {/* Immediate Trust Signals */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="text-center">
                    <Shield className="w-8 h-8 text-blue-200 mx-auto mb-2" />
                    <div className="text-lg font-bold">GAF Master Elite</div>
                    <div className="text-sm text-blue-200">Top 2% Nationwide</div>
                  </div>
                  <div className="text-center">
                    <Award className="w-8 h-8 text-blue-200 mx-auto mb-2" />
                    <div className="text-lg font-bold">50-Year Warranty</div>
                    <div className="text-sm text-blue-200">Available Options</div>
                  </div>
                  <div className="text-center">
                    <Users className="w-8 h-8 text-blue-200 mx-auto mb-2" />
                    <div className="text-lg font-bold">5,000+</div>
                    <div className="text-sm text-blue-200">Satisfied Customers</div>
                  </div>
                  <div className="text-center">
                    <Clock className="w-8 h-8 text-blue-200 mx-auto mb-2" />
                    <div className="text-lg font-bold">25+ Years</div>
                    <div className="text-sm text-blue-200">Local Experience</div>
                  </div>
                </div>

                {/* Featured Customer Quote */}
                <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white">
                  <CardContent className="p-6">
                    <div className="flex items-center mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                      <span className="ml-2 text-sm text-blue-200">Google Review</span>
                    </div>
                    <blockquote className="text-lg italic mb-4">
                      "Best decision we ever made. Safeway's quality and professionalism are unmatched. 
                      Three years later, our roof still looks perfect!"
                    </blockquote>
                    <cite className="font-semibold">— The Johnson Family, Bay Shore</cite>
                  </CardContent>
                </Card>

                {/* Primary CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg" 
                    className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 text-lg font-semibold"
                    onClick={() => window.open('tel:+18006456676', '_self')}
                  >
                    <PhoneCall className="w-5 h-5 mr-2" />
                    Ready to Schedule? Call Now
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 text-lg font-semibold"
                  >
                    <FileText className="w-5 h-5 mr-2" />
                    Review Your Estimate
                  </Button>
                </div>
              </div>

              {/* Contact Info Card */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-slate-900 flex items-center">
                    <CheckCircle className="w-6 h-6 mr-2 text-green-600" />
                    Ready to Move Forward?
                  </CardTitle>
                  <p className="text-slate-600">Your estimate is ready - let's schedule your project</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                      <div className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">1</div>
                      <div>
                        <h4 className="font-semibold text-slate-900 text-sm">Call to Accept</h4>
                        <p className="text-xs text-slate-600">Discuss your estimate details</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                      <div className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">2</div>
                      <div>
                        <h4 className="font-semibold text-slate-900 text-sm">Schedule Install</h4>
                        <p className="text-xs text-slate-600">We can start within 1-2 weeks</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                      <div className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">3</div>
                      <div>
                        <h4 className="font-semibold text-slate-900 text-sm">Enjoy Your New Roof</h4>
                        <p className="text-xs text-slate-600">With 50-year warranty protection</p>
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    size="lg" 
                    className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
                    onClick={() => window.open('tel:+18006456676', '_self')}
                  >
                    <PhoneCall className="w-5 h-5 mr-2" />
                    Call (800) 645-6676
                  </Button>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-blue-600" />
                      <span className="text-slate-700">Serving All of Long Island</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      <span className="text-slate-700">Same-Day Estimates Available</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-blue-600" />
                      <span className="text-slate-700">Licensed, Insured & Bonded</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Why Choose Us Over Competitors */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                What Sets Us Apart From Other Estimates You've Received
              </h2>
              <p className="text-xl text-slate-600">
                Here's exactly why Safeway Roofing is your best choice
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {competitiveAdvantages.map((item, index) => (
                <Card key={index} className="border-2 border-green-100 hover:border-green-200 transition-colors">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        {item.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-slate-900 mb-4">{item.advantage}</h3>
                        <div className="space-y-4">
                          <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                            <h4 className="font-bold text-green-800 mb-2 flex items-center">
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Safeway Roofing
                            </h4>
                            <p className="text-green-700 font-medium">{item.us}</p>
                          </div>
                          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <h4 className="font-semibold text-gray-800 mb-2">Typical Competitors</h4>
                            <p className="text-gray-600">{item.others}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Before/After Gallery with Social Proof */}
        <section className="py-20 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                See Our Work in Your Neighborhood
              </h2>
              <p className="text-xl text-slate-600">
                Real projects from satisfied Long Island homeowners just like you
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {galleryData.projects.map((project, index) => (
                <Card key={index} className="overflow-hidden group">
                  <div className="relative h-64">
                    <img 
                      src={`/images/projects/${project.images[0]}`}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <h3 className="text-lg font-bold mb-2">{project.title}</h3>
                      <p className="text-sm text-gray-200 mb-2">
                        <MapPin className="w-3 h-3 inline mr-1" />
                        {project.location}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {project.tags.slice(0, 2).map((tag, tagIndex) => (
                          <Badge key={tagIndex} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        ))}
                        <span className="ml-2 text-sm text-slate-600">5.0</span>
                      </div>
                      <Badge className="bg-green-100 text-green-800">Completed</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center mt-12">
              <p className="text-lg text-slate-600 mb-6">
                <Heart className="w-5 h-5 inline text-red-500 mr-2" />
                Your neighbors in Nassau & Suffolk Counties trust us with their homes
              </p>
              <Button 
                size="lg" 
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4"
                onClick={() => window.open('tel:+18006456676', '_self')}
              >
                <PhoneCall className="w-5 h-5 mr-2" />
                Join Our Satisfied Customers
              </Button>
            </div>
          </div>
        </section>

        {/* Customer Success Stories */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                Success Stories From Your Community
              </h2>
              <p className="text-xl text-slate-600">
                Real customers, real results, real peace of mind
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12">
              {successStories.map((story, index) => (
                <Card key={index} className="overflow-hidden border-2 border-blue-100">
                  <div className="grid md:grid-cols-5">
                    <div className="md:col-span-2">
                      <img 
                        src={story.image}
                        alt={`${story.customer} project`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="md:col-span-3 p-8">
                      <div className="flex items-center mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                        ))}
                        <Badge className="ml-2 bg-blue-100 text-blue-800">Verified Customer</Badge>
                      </div>
                      <blockquote className="text-lg text-slate-700 mb-6 italic">
                        "{story.quote}"
                      </blockquote>
                      <div className="mb-6">
                        <h4 className="font-bold text-slate-900">{story.customer}</h4>
                        <p className="text-slate-600 text-sm flex items-center">
                          <MapPin className="w-3 h-3 mr-1" />
                          {story.location}
                        </p>
                      </div>
                      <div className="space-y-3 text-sm">
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-red-600 min-w-fit">Challenge:</span>
                          <span className="text-slate-600">{story.problem}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-blue-600 min-w-fit">Solution:</span>
                          <span className="text-slate-600">{story.solution}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-green-600 min-w-fit">Result:</span>
                          <span className="text-slate-600">{story.result}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Comprehensive Certifications & Credentials */}
        <CertificationsCredentials />

        {/* Customer Testimonials */}
        <Testimonials />

        {/* About the Team - Personal Touch */}
        <TeamAbout />

        {/* Guarantee & Next Steps */}
        <section className="py-20 bg-blue-600">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <h2 className="text-4xl font-bold mb-6">
                  Our Guarantee to You
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-blue-200 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold mb-2">100% Satisfaction Guarantee</h3>
                      <p className="text-blue-100">We don't just guarantee our work — we guarantee your complete satisfaction with every aspect of your roofing project.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-blue-200 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Complete Cleanup Promise</h3>
                      <p className="text-blue-100">Your property will be cleaner than when we started. We use magnetic tools and thorough cleanup processes.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-blue-200 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Timeline Commitment</h3>
                      <p className="text-blue-100">We start when promised and finish on schedule. Any delays are communicated immediately with solutions.</p>
                    </div>
                  </div>
                </div>
              </div>

              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-slate-900 flex items-center">
                    <Trophy className="w-6 h-6 text-blue-600 mr-2" />
                    Ready to Move Forward?
                  </CardTitle>
                  <p className="text-slate-600">Here's how to get started with your roofing project</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                      <div className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">1</div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Call to Accept Estimate</h4>
                        <p className="text-sm text-slate-600">Discuss any questions about your project</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                      <div className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">2</div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Schedule Installation</h4>
                        <p className="text-sm text-slate-600">We can typically start within 1-2 weeks</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                      <div className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">3</div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Enjoy Your New Roof</h4>
                        <p className="text-sm text-slate-600">With warranty protection for decades</p>
                      </div>
                    </div>
                  </div>

                  <Button 
                    size="lg" 
                    className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
                    onClick={() => window.open('tel:+18006456676', '_self')}
                  >
                    <PhoneCall className="w-5 h-5 mr-2" />
                    Call (800) 645-6676 Now
                  </Button>
                  
                  <div className="text-center text-sm text-slate-500">
                    <Shield className="w-4 h-4 inline mr-1" />
                    Licensed #H18A0600100 • Fully Insured • GAF Master Elite
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Mobile Sticky FAB */}
        <MobileStickyFAB />
      </main>
    </>
  );
};

export default WelcomeLanding;